'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { isMediaImageDecoded, rememberDecodedMediaImage } from '@/lib/media-delivery';
import { isExpiredSignedMediaUrl } from '@/lib/expiring-media-url';

type LoadedImage = { owner: string; url: string; rank: number };

/** Decode upgrades once per session; remounts start with the best known representation. */
export function useProgressiveMediaImage({ owner, thumbnail, preview, original, waitForDelivery = false }: {
  owner: string; thumbnail?: string; preview?: string; original?: string; waitForDelivery?: boolean;
}) {
  const stages = useMemo(() => {
    const byUrl = new Map<string, { url: string; rank: number }>();
    [thumbnail, preview, waitForDelivery ? undefined : original].forEach((url, rank) => {
      if (url && !isExpiredSignedMediaUrl(url)) byUrl.set(url, { url, rank });
    });
    return [...byUrl.values()].sort((a, b) => a.rank - b.rank);
  }, [thumbnail, preview, original, waitForDelivery]);
  const cached = [...stages].reverse().find(stage => isMediaImageDecoded(stage.url));
  const [loaded, setLoaded] = useState<LoadedImage | null>(() => cached ? { owner, ...cached } : null);
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;

  useEffect(() => {
    let cancelled = false;
    let active: HTMLImageElement | undefined;
    let cancelPending: (() => void) | undefined;
    const publish = (rank: number, url: string) => {
      const current = loadedRef.current;
      if (current?.owner === owner && !isExpiredSignedMediaUrl(current.url)
        && (current.rank > rank || (current.rank === rank && current.url === url))) return;
      const next = { owner, rank, url };
      loadedRef.current = next;
      setLoaded(next);
    };
    // A decoded original wins before loading any intermediate representation.
    const best = [...stages].reverse().find(stage => isMediaImageDecoded(stage.url));
    if (best) publish(best.rank, best.url);
    const load = async () => {
      for (const { rank, url } of stages) {
        if (cancelled) return;
        if (isExpiredSignedMediaUrl(url)) continue;
        const current = loadedRef.current;
        if (current?.owner === owner && !isExpiredSignedMediaUrl(current.url)
          && (current.rank > rank || (current.rank === rank && current.url === url))) continue;
        if (isMediaImageDecoded(url)) { publish(rank, url); continue; }
        const image = new Image();
        active = image;
        image.decoding = 'async';
        try {
          await new Promise<void>((resolve, reject) => {
            const timer = window.setTimeout(() => finish(new Error('Image timed out')), 30_000);
            const finish = (error?: Error) => {
              window.clearTimeout(timer);
              image.onload = null;
              image.onerror = null;
              cancelPending = undefined;
              if (error) { image.removeAttribute('src'); reject(error); } else resolve();
            };
            cancelPending = () => finish(new Error('Image cancelled'));
            image.onload = () => finish();
            image.onerror = () => finish(new Error('Image unavailable'));
            image.src = url;
          });
          if (image.decode) await image.decode();
          if (cancelled) return;
          rememberDecodedMediaImage(url);
          publish(rank, url);
        } catch {
          // Keep the last decoded representation while trying the next one.
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      cancelPending?.();
      if (active) { active.onload = null; active.onerror = null; active.removeAttribute('src'); }
    };
  }, [owner, stages]);

  const visible = loaded?.owner === owner && !isExpiredSignedMediaUrl(loaded.url) ? loaded : null;
  if (cached && (!visible || cached.rank > visible.rank)) return cached.url;
  return visible?.url || stages.find(stage => !isExpiredSignedMediaUrl(stage.url))?.url || '';
}
